/**
 * Purchase orders service (shop.purchase_orders + lines + revisions).
 *
 * All mutations go through SECURITY DEFINER RPCs so the buyer-vs-vendor
 * permission checks and revision-log writes stay in one place.
 *
 * Reads are RLS-scoped: buyer sees their own POs, vendor team sees
 * POs addressed to them.
 */

import { supabase } from '../client';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';
import { notifyShopEvent, type ShopNotifyEvent } from './shopNotify';
import type {
  ShopPoRevision,
  ShopPoStatus,
  ShopPurchaseOrder,
  ShopPurchaseOrderLine,
} from './shopTypes';

export interface ListShopPurchaseOrdersInput extends KeysetInput {
  vendorId?: string;
  buyerUserId?: string;
  status?: ShopPoStatus;
}

export async function listShopPurchaseOrders(
  input: ListShopPurchaseOrdersInput,
): Promise<ListResult<ShopPurchaseOrder>> {
  const pageSize = clampPageSize(input.pageSize);
  let q = (supabase() as any)
    .schema('shop')
    .from('purchase_orders')
    .select('*', selectCountOption(input.countMode))
    .is('archived_at', null);

  if (input.vendorId) q = q.eq('vendor_id', input.vendorId);
  if (input.buyerUserId) q = q.eq('buyer_user_id', input.buyerUserId);
  if (input.status) q = q.eq('status', input.status);

  q = applyKeyset(q, input.cursor, 'updated_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ShopPurchaseOrder[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'updated_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

export async function getShopPurchaseOrder(id: string): Promise<ShopPurchaseOrder | null> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('purchase_orders')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as ShopPurchaseOrder | null;
}

export async function listShopPurchaseOrderLines(
  poId: string,
): Promise<ShopPurchaseOrderLine[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('purchase_order_lines')
    .select('*')
    .eq('po_id', poId)
    .order('sort_order', { ascending: true })
    .order('id', { ascending: true })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as ShopPurchaseOrderLine[];
}

export async function listShopPoRevisions(
  poId: string,
): Promise<ShopPoRevision[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('po_revisions')
    .select('*')
    .eq('po_id', poId)
    .order('created_at', { ascending: true })
    .order('id', { ascending: true })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as ShopPoRevision[];
}

export interface PoLineInput {
  productId: string;
  qty: number;
  unitPriceCents?: number;
  lineNotes?: string;
}

function toJsonLine(l: PoLineInput) {
  return {
    product_id: l.productId,
    qty: l.qty,
    unit_price_cents: l.unitPriceCents,
    line_notes: l.lineNotes,
  };
}

/**
 * Fire a shop-notify event without throwing. We deliberately swallow
 * notification errors here because the underlying state-transition RPC
 * (submit / respond / accept) has already committed and the operator's
 * action succeeded. Surfacing a notification failure as a thrown error
 * would mislead them into thinking the PO move itself failed.
 *
 * If we ever need observability on these we can pipe the warn into
 * core.errors or a future shop.notification_log table -- for now the
 * browser console captures the failure and `core.email_messages` keeps
 * the authoritative record when the queue step did succeed.
 */
async function fireShopEventSafely(
  event: ShopNotifyEvent,
  refId: string,
  vendorNote?: string,
): Promise<void> {
  try {
    const result = await notifyShopEvent({ event, refId, vendorNote });
    if (!result.ok) {
      console.warn('[shopPurchaseOrders] shop-notify failed', event, refId, result.error);
    }
  } catch (err) {
    console.warn('[shopPurchaseOrders] shop-notify threw', event, refId, err);
  }
}

export async function submitShopPurchaseOrder(input: {
  vendorId: string;
  lines: PoLineInput[];
  buyerNote?: string;
  /**
   * Buyer org under which to issue this PO. The RPC stamps this on
   * the PO row and uses the org's configured `po_prefix`
   * (`core.org_settings.po_prefix`) for the displayed `po_code`. If
   * omitted, the RPC resolves to the caller's single org membership
   * and errors when there are multiple.
   */
  buyerOrgId?: string | null;
}): Promise<string> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_buyer_submit_po', {
      p_vendor_id: input.vendorId,
      p_lines: input.lines.map(toJsonLine),
      p_buyer_note: input.buyerNote ?? null,
      p_buyer_org_id: input.buyerOrgId ?? null,
    });
  if (error) throw error;
  const poId = data as string;
  await fireShopEventSafely('po_submitted', poId);
  return poId;
}

export async function vendorRespondShopPurchaseOrder(input: {
  poId: string;
  action: 'approve' | 'deny' | 'revise';
  lines?: PoLineInput[];
  vendorNote?: string;
}): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_respond_po', {
      p_po_id: input.poId,
      p_action: input.action,
      p_lines: input.lines ? input.lines.map(toJsonLine) : null,
      p_vendor_note: input.vendorNote ?? null,
    });
  if (error) throw error;

  // `approve` doesn't fire an email -- the buyer still has to attach
  // shipping before the order is real, and the dashboard surfaces it.
  // `deny` and `revise` both notify the buyer; we pass the vendorNote
  // explicitly so the email reflects what the vendor JUST typed even if
  // the row hasn't refreshed locally yet.
  if (input.action === 'deny') {
    await fireShopEventSafely('po_denied', input.poId, input.vendorNote);
  } else if (input.action === 'revise') {
    await fireShopEventSafely('po_revised', input.poId, input.vendorNote);
  }
}

export async function buyerAcceptShopPurchaseOrder(input: {
  poId: string;
  buyerNote?: string;
}): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_buyer_accept_po', {
      p_po_id: input.poId,
      p_buyer_note: input.buyerNote ?? null,
    });
  if (error) throw error;
  await fireShopEventSafely('po_accepted', input.poId);
}

export async function withdrawShopPurchaseOrder(input: {
  poId: string;
  note?: string;
}): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_withdraw_po', {
      p_po_id: input.poId,
      p_note: input.note ?? null,
    });
  if (error) throw error;
}
